class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.scrollLockApplied = false;
    this.originalBodyPaddingRight = '';
    this.relatedProductsScrollPosition = null;
    this.relatedProductsScrollRestoreTimeout = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (!containerToTrapFocusOn || !focusElement) return;
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockBodyScroll();
  }

  close() {
    const scrollPosition = this.relatedProductsScrollPosition;
    const preserveRelatedProductsScroll = Boolean(scrollPosition);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    this.classList.remove('active');

    if (preserveRelatedProductsScroll) {
      removeTrapFocus();
      this.unlockBodyScroll();

      // Restoring focus to an off-screen quick-add button can make mobile Safari
      // scroll to that button, even when preventScroll is supplied. Keyboard
      // users on desktop still return to the original trigger.
      if (!isMobile && this.activeElement?.isConnected) {
        this.activeElement.focus({ preventScroll: true });
      }

      const restoreScrollPosition = () => window.scrollTo(scrollPosition.left, scrollPosition.top);
      restoreScrollPosition();

      // Focus and drawer layout can both settle one or two frames after close.
      // Re-apply the captured position on every viewport so neither desktop nor
      // mobile can jump back to the main product form.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(restoreScrollPosition);
      });

      window.clearTimeout(this.relatedProductsScrollRestoreTimeout);
      this.relatedProductsScrollRestoreTimeout = window.setTimeout(() => {
        restoreScrollPosition();
        this.relatedProductsScrollPosition = null;
        this.relatedProductsScrollRestoreTimeout = null;
      }, 250);
    } else {
      removeTrapFocus(this.activeElement);
      this.unlockBodyScroll();
    }
  }

  lockBodyScroll() {
    if (this.scrollLockApplied) return;

    const scrollbarWidth = Math.max(window.innerWidth - document.documentElement.clientWidth, 0);
    this.originalBodyPaddingRight = document.body.style.paddingRight;

    if (scrollbarWidth > 0) {
      const currentPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    document.body.classList.add('overflow-hidden');
    this.scrollLockApplied = true;
  }

  unlockBodyScroll() {
    document.body.classList.remove('overflow-hidden');

    if (this.originalBodyPaddingRight) {
      document.body.style.paddingRight = this.originalBodyPaddingRight;
    } else {
      document.body.style.removeProperty('padding-right');
    }

    this.scrollLockApplied = false;
    this.originalBodyPaddingRight = '';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Replace the empty drawer markup before switching its layout state. This keeps
    // the first quick-add render atomic and avoids briefly painting an empty drawer
    // with the filled-cart layout.
    this.classList.remove('is-empty');
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element, sourceContext) {
    this.activeElement = element;
    const isRelatedProductsQuickAdd =
      sourceContext === 'related_products' ||
      element?.closest?.('[data-collection-quick-add-context="related_products"]');

    this.relatedProductsScrollPosition = isRelatedProductsQuickAdd
      ? { left: window.scrollX, top: window.scrollY }
      : null;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
